// Detect whether we're on the admin subdomain (switch.offer.express)
// or the public marketing site (offer.express / www.offer.express).
//
// The admin subdomain serves ONLY /login + /admin + /partner. All other paths
// on that host redirect to /login. On the public host, /login and the admin
// routes redirect to the admin subdomain so bots and casual browsers can't
// discover the admin panel from the public site.
//
// For local dev / Emergent preview, add `?admin=1` to any URL to force the
// admin mode (persisted for the session).

const ADMIN_PREFIX = "switch.";
const LOANS_PREFIX = "loans.";
const OVERRIDE_KEY = "trc_admin_mode";
const LOANS_OVERRIDE_KEY = "trc_loans_mode";

// Only do cross-subdomain redirects on the production domain.
// On Emergent preview, localhost, or any *.preview.emergentagent.com host,
// the subdomain doesn't exist — so we let a single host serve both modes
// gated only by the ?admin=1 override.
function _isProdDomain() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h.endsWith("offer.express");
}

function _readOverride() {
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get("admin") === "1") {
      sessionStorage.setItem(OVERRIDE_KEY, "1");
      return true;
    }
    if (p.get("admin") === "0") {
      sessionStorage.removeItem(OVERRIDE_KEY);
      return false;
    }
    return sessionStorage.getItem(OVERRIDE_KEY) === "1";
  } catch {
    return false;
  }
}

export function isAdminHost() {
  if (typeof window === "undefined") return false;
  if (_readOverride()) return true;
  return _isProdDomain() && window.location.hostname.startsWith(ADMIN_PREFIX);
}

/** True when we're on loans.offer.express (or forcing ?loans=1 in preview). */
export function isLoansHost() {
  if (typeof window === "undefined") return false;
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get("loans") === "1") { sessionStorage.setItem(LOANS_OVERRIDE_KEY, "1"); return true; }
    if (p.get("loans") === "0") { sessionStorage.removeItem(LOANS_OVERRIDE_KEY); }
    if (sessionStorage.getItem(LOANS_OVERRIDE_KEY) === "1") return true;
  } catch { /* ignore */ }
  return _isProdDomain() && window.location.hostname.startsWith(LOANS_PREFIX);
}

/** True only when the subdomain split is actually enforceable (prod). */
export function shouldCrossHost() {
  return _isProdDomain();
}

/** Public-site canonical host (used when we need to redirect off admin). */
export function publicHost() {
  const h = window.location.hostname;
  if (h.startsWith(ADMIN_PREFIX)) return h.slice(ADMIN_PREFIX.length);
  return h;
}

/** Admin-site canonical host. */
export function adminHost() {
  const h = window.location.hostname;
  if (h.startsWith(ADMIN_PREFIX)) return h;
  // strip leading `www.` before prepending `switch.`
  const clean = h.startsWith("www.") ? h.slice(4) : h;
  return ADMIN_PREFIX + clean;
}

/** Build a fully-qualified URL on the admin host, preserving protocol + port. */
export function urlOnAdmin(path = "/") {
  const { protocol, port } = window.location;
  const p = port && !["80", "443", ""].includes(port) ? `:${port}` : "";
  return `${protocol}//${adminHost()}${p}${path}`;
}

/** Build a fully-qualified URL on the public host. */
export function urlOnPublic(path = "/") {
  const { protocol, port } = window.location;
  const p = port && !["80", "443", ""].includes(port) ? `:${port}` : "";
  return `${protocol}//${publicHost()}${p}${path}`;
}
